import { useEffect, useState } from 'react';
import { Download, X, Share2, Plus } from 'lucide-react';
import { isStandaloneDisplay } from '../../pwa';

const DISMISS_KEY = 'aedu_pwa_install_dismissed_at';
const DISMISS_TTL_MS = 7 * 24 * 60 * 60 * 1000;  // 7 days
const IOS_SEEN_KEY  = 'aedu_pwa_ios_hint_seen';

/**
 * InstallAppBanner — appears once we're allowed to install ActanonEdu as a PWA.
 * Chrome/Android → uses window.__aeduInstallPrompt (captured in pwa.js).
 * iOS Safari → renders a tiny "Add to Home Screen" walkthrough instead.
 * Both dismissals stick for 7 days.
 */
export default function InstallAppBanner() {
  const [installable, setInstallable] = useState(false);
  const [iosHint, setIosHint] = useState(false);

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    // If we're already running as an installed PWA, never show anything.
    if (isStandaloneDisplay()) return undefined;

    // Recently dismissed? Honour it.
    const dAt = parseInt(localStorage.getItem(DISMISS_KEY) || '0', 10);
    if (dAt && Date.now() - dAt < DISMISS_TTL_MS) return undefined;

    // 1) Native prompt path (Chrome / Edge / Android)
    if (window.__aeduInstallPrompt) setInstallable(true);
    const onInstallable = () => setInstallable(true);
    const onInstalled   = () => setInstallable(false);
    window.addEventListener('aedu:installable', onInstallable);
    window.addEventListener('aedu:installed',   onInstalled);

    // 2) iOS walkthrough — iPhone Safari doesn't fire beforeinstallprompt.
    //    v90.1 — robust iOS detection. Needs to cover:
    //      • iPhone/iPod running Safari
    //      • iPad on iPadOS 13+ (defaults to Mac desktop UA — sniff via
    //        Mac + maxTouchPoints>1)
    //      • Chrome/Firefox on iOS (CriOS/FxiOS) — same WebKit engine
    //        AND same "Add to Home Screen" flow, so we ALSO show the
    //        hint there (they can't natively install either).
    //      • Only Safari-family exposes `window.navigator.standalone`
    //        — presence of that property is the most reliable signal
    //        that we're on iOS WebKit at all.
    const ua = navigator.userAgent || '';
    const isIphonePod = /iPhone|iPod/i.test(ua);
    const isIpadOs = /iPad/i.test(ua) || (/Mac/i.test(ua) && (navigator.maxTouchPoints || 0) > 1);
    const hasStandaloneApi = typeof window.navigator.standalone !== 'undefined';
    const isIos = (isIphonePod || isIpadOs || hasStandaloneApi);
    if (isIos && !localStorage.getItem(IOS_SEEN_KEY)) setIosHint(true);

    return () => {
      window.removeEventListener('aedu:installable', onInstallable);
      window.removeEventListener('aedu:installed',   onInstalled);
    };
  }, []);

  const dismiss = () => {
    localStorage.setItem(DISMISS_KEY, String(Date.now()));
    setInstallable(false);
    setIosHint(false);
  };

  const install = async () => {
    try {
      await window.__aeduInstallPrompt?.prompt();
      await window.__aeduInstallPrompt?.whenChoice();
      setInstallable(false);
    } catch { /* user cancelled */ }
    localStorage.setItem(DISMISS_KEY, String(Date.now()));
  };

  if (!installable && !iosHint) return null;

  return (
    <div
      className="fixed inset-x-0 bottom-0 z-[60] px-3 pointer-events-none"
      style={{ paddingBottom: 'calc(0.75rem + env(safe-area-inset-bottom, 0px))' }}
      data-testid="pwa-install-banner"
    >
      <div className="mx-auto max-w-md rounded-xl border border-emerald-500/30 bg-slate-900/95 backdrop-blur px-4 py-3 shadow-xl shadow-black/40 pointer-events-auto">
        <div className="flex items-start gap-3">
          <div className="h-9 w-9 rounded-lg bg-emerald-500/20 text-emerald-300 flex items-center justify-center flex-shrink-0">
            <Download className="h-4 w-4" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-sm font-semibold text-slate-100">
              Install ActanonEdu
            </div>
            {installable ? (
              <p className="text-[11px] text-slate-400 mt-0.5 leading-relaxed">
                Add ActanonEdu to your home screen for a faster, full-screen experience — no browser tabs.
              </p>
            ) : (
              <p className="text-[11px] text-slate-400 mt-0.5 leading-relaxed flex items-center gap-1">
                Tap <Share2 className="inline h-3 w-3 mx-0.5 text-emerald-300" /> then
                <span className="mx-1 inline-flex items-center gap-0.5 text-emerald-300">
                  <Plus className="h-3 w-3" /> Add to Home Screen
                </span>
                to install.
              </p>
            )}
            {installable && (
              <div className="mt-2 flex items-center gap-2">
                <button
                  type="button"
                  onClick={install}
                  className="text-xs font-semibold px-3 py-1.5 rounded-md bg-emerald-600 hover:bg-emerald-700 text-white"
                  data-testid="pwa-install-btn"
                >
                  Install
                </button>
                <button
                  type="button"
                  onClick={dismiss}
                  className="text-xs px-2 py-1.5 rounded-md text-slate-400 hover:text-slate-200"
                  data-testid="pwa-install-not-now"
                >
                  Not now
                </button>
              </div>
            )}
          </div>
          <button
            type="button"
            onClick={() => {
              if (iosHint) localStorage.setItem(IOS_SEEN_KEY, '1');
              dismiss();
            }}
            className="text-slate-500 hover:text-slate-300 p-2 -m-1 flex items-center justify-center"
            style={{ minWidth: 44, minHeight: 44 }}
            aria-label="Dismiss"
            data-testid="pwa-install-dismiss"
          >
            <X className="h-4 w-4 pointer-events-none" />
          </button>
        </div>
      </div>
    </div>
  );
}
